// src/pages/admin/quests/QuestsDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Title from "../../components/Title";
import Body from "../../layout/Body";
import SearchBar from "../../../../components/common/searchBar/SearchBar";
import Typography from "../../../../components/common/text/Typography";
import Button from "../../../../components/common/button/Button";
import Banner from "../../../../components/layout/ui/Banner";
import { ReactComponent as EditIcon } from "../../../../assets/images/icons/editIcon.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/images/icons/dustbin.svg";

import { CACHE_QUERY_OPTIONS } from "../../../../constants";
import { formatDateTimeWithTimeZone } from "../../../../utils/generalUtils";
import { setColor, setFont } from "../../../../assets/styles/styles";

// You’ll add this function in your QuestController.js (admin)
import {
    getAllQuestsAdmin,
    deleteQuestAdmin,
    toggleQuestActive,
    getQuestWithSteps,
} from "../../../../api/QuestController";

const QuestsDashboard = () => {
    const navigate = useNavigate();
    const token = useSelector((state) => state?.user?.token);

    console.log("[QuestsDashboard] Component mounted");

    const [searchInput, setSearchInput] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [totalNumber, setTotalNumber] = useState(0);
    const [startingRooms, setStartingRooms] = useState({}); // questId -> starting room id

    const [openBanner, setOpenBanner] = useState("");
    const [bannerMessage, setBannerMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // Room conflict state for "one active quest per room" protection
    const [roomConflict, setRoomConflict] = useState(null);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [pendingActivateQuestId, setPendingActivateQuestId] = useState(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteText, setDeleteText] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [pendingDeleteQuestId, setPendingDeleteQuestId] = useState(null);
    const [pendingDeleteQuestName, setPendingDeleteQuestName] = useState("");

    const {
        data: allQuestsResponse,
        error,
        isLoading,
        refetch,
    } = useQuery(["allQuestsAdmin"], () => getAllQuestsAdmin(token), {
        ...CACHE_QUERY_OPTIONS(),
        enabled: !!token,
    });

    const allQuests = useMemo(() => {
        // Expect response like: { status: 200, data: { quests: [...] } } OR { quests: [...] }
        const raw = allQuestsResponse?.data?.quests || allQuestsResponse?.quests || [];
        return Array.isArray(raw) ? raw : [];
    }, [allQuestsResponse]);

    useEffect(() => {
        // Always refetch quests on mount
        refetch();
    }, []);

    useEffect(() => {
        const rows = [];
        if (allQuests && allQuests.length > 0) {
            for (let i = 0; i < allQuests.length; i += 1) {
                const q = { ...allQuests[i] };
                // Normalize common ID naming
                q.questId = q.questId || q.id;

                // Normalize field names (API may use snake_case or camelCase)
                q.startingRoomId = q.startingRoomId || q.starting_room_id || "";
                q.description = q.description || "";
                q.isActive = q.isActive ?? q.is_active ?? false;

                // Format dates if present
                if (q.createdAt) q.createdAt = formatDateTimeWithTimeZone(q.createdAt);
                if (q.updatedAt) q.updatedAt = formatDateTimeWithTimeZone(q.updatedAt);

                rows.push(q);
            }
        }

        // Fetch starting room from first step for each quest if not already loaded
        const fetchStartingRooms = async () => {
            const missing = rows.filter((q) => !startingRooms[q.questId]);
            if (missing.length > 0) {
                const updates = {};
                await Promise.all(
                    missing.map(async (q) => {
                        try {
                            const resp = await getQuestWithSteps(token, q.questId);
                            const steps = resp?.data?.steps || [];
                            if (steps.length > 0) {
                                updates[q.questId] = steps[0].room_id || steps[0].roomId || "";
                            }
                        } catch {
                            /* none */
                        }
                    })
                );
                if (Object.keys(updates).length > 0) setStartingRooms((prev) => ({ ...prev, ...updates }));
            }
        };
        fetchStartingRooms();

        // Sort newest first (createdAt fallback updatedAt)
        rows.sort((a, b) => {
            const da = new Date(a.createdAt || a.updatedAt || 0).getTime();
            const db = new Date(b.createdAt || b.updatedAt || 0).getTime();
            return db - da;
        });

        if (!searchInput || searchInput.length === 0) {
            setFilteredData(rows);
            setTotalNumber(rows.length);
        } else {
            const text = searchInput.toLowerCase();
            const filtered = rows.filter((q) => {
                const id = (q.questId || "").toLowerCase();
                const name = (q.name || "").toLowerCase();
                const desc = (q.description || "").toLowerCase();
                const startRoom = (
                    startingRooms[q.questId] ||
                    q.startingRoomId ||
                    q.starting_room_id ||
                    ""
                ).toLowerCase();
                const active = String(q.isActive ?? q.is_active ?? "").toLowerCase();
                return (
                    id.includes(text) ||
                    name.includes(text) ||
                    desc.includes(text) ||
                    startRoom.includes(text) ||
                    active.includes(text)
                );
            });

            setFilteredData(filtered);
            setTotalNumber(filtered.length);
        }
    }, [allQuests, searchInput, startingRooms, token]);

    const handleSearchChange = (value) => {
        setSearchInput(value);
    };

    const handleCreateNewQuest = () => {
        console.log("[QuestsDashboard] Creating new quest - navigating to /console/quest-builder");
        navigate("/console/quest-builder");
    };

    const handleEditQuest = (questId) => {
        console.log(`[QuestsDashboard] Editing quest - navigating to /console/quest-builder/${questId}`);
        navigate(`/console/quest-builder/${questId}`);
    };

    // Helper function to get starting room for a quest (from quest or first step)
    const getQuestStartingRoom = async (questData) => {
        const qId = questData.questId || questData.id;
        let qStartingRoom = (questData.starting_room_id || questData.startingRoomId || "").trim();

        // If starting_room_id is empty, fetch the quest steps to get first step's room_id
        if (!qStartingRoom) {
            // Check if we already have it cached
            if (startingRooms[qId]) {
                return startingRooms[qId];
            }
            try {
                const stepsResponse = await getQuestWithSteps(token, qId);
                if (stepsResponse?.status === 200) {
                    const steps = stepsResponse?.data?.steps || [];
                    if (steps.length > 0) {
                        const sortedSteps = [...steps].sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));
                        qStartingRoom = (sortedSteps[0].room_id || sortedSteps[0].roomId || "").trim();
                    }
                }
            } catch (err) {
                console.error("[QuestsDashboard] Error fetching steps for quest:", qId, err);
            }
        }
        return qStartingRoom;
    };

    // Check if activating a quest would cause a room conflict
    const checkRoomConflict = async (questIdToActivate) => {
        // Find the quest we're trying to activate
        const questToActivate = allQuests.find((q) => (q.questId || q.id) === questIdToActivate);
        if (!questToActivate) return null;

        const targetRoomId = await getQuestStartingRoom(questToActivate);
        if (!targetRoomId) return null;

        // Check all other active quests for the same room
        const otherActiveQuests = allQuests.filter((q) => {
            const qId = q.questId || q.id;
            const qIsActive = q.isActive ?? q.is_active ?? false;
            return qIsActive && qId !== questIdToActivate;
        });

        // Resolve starting rooms for all active quests
        const questsWithRooms = await Promise.all(
            otherActiveQuests.map(async (q) => {
                const resolvedRoom = await getQuestStartingRoom(q);
                return { ...q, resolvedStartingRoom: resolvedRoom };
            })
        );

        // Find conflict
        const conflictQuest = questsWithRooms.find((q) => q.resolvedStartingRoom === targetRoomId);
        if (conflictQuest) {
            return {
                questId: conflictQuest.questId || conflictQuest.id,
                name: conflictQuest.name || "Unnamed Quest",
            };
        }
        return null;
    };

    const handleToggleQuestActive = async (questId, currentIsActive) => {
        try {
            console.log(
                `[QuestsDashboard] Toggling quest ${questId} active state from ${currentIsActive} to ${!currentIsActive}`
            );
            setErrorMessage("");
            const nextActive = !currentIsActive;

            // If activating, check for room conflict first
            if (nextActive) {
                const conflict = await checkRoomConflict(questId);
                if (conflict) {
                    console.log("[QuestsDashboard] Room conflict found:", conflict);
                    setRoomConflict(conflict);
                    setPendingActivateQuestId(questId);
                    setShowConflictModal(true);
                    return;
                }
            }

            const response = await toggleQuestActive(token, questId, nextActive);

            if (response && (response.status === 200 || response.status === 204)) {
                setBannerMessage(nextActive ? "Quest activated" : "Quest deactivated");
                setOpenBanner("MESSAGE");
                refetch();
                return;
            }

            setBannerMessage("Error toggling quest active state");
            setOpenBanner("ERROR");
        } catch (e) {
            console.error(e);
            setBannerMessage("Error toggling quest active state");
            setOpenBanner("ERROR");
        }
    };

    // Handler to deactivate a conflicting quest and then activate the pending one
    const handleDeactivateConflict = async () => {
        if (!roomConflict || !pendingActivateQuestId) return;

        console.log(`[QuestsDashboard] Deactivating conflicting quest: ${roomConflict.questId}`);
        try {
            const response = await toggleQuestActive(token, roomConflict.questId, false);
            if (response?.status === 200 || response?.status === 204) {
                setBannerMessage(`Deactivated conflicting quest: ${roomConflict.name}`);
                setOpenBanner("MESSAGE");
                setRoomConflict(null);
                setShowConflictModal(false);
                setPendingActivateQuestId(null);
                refetch();
            } else {
                setBannerMessage("Error deactivating conflicting quest");
                setOpenBanner("ERROR");
            }
        } catch (err) {
            console.error("[QuestsDashboard] Error deactivating conflicting quest:", err);
            setBannerMessage("Error deactivating conflicting quest");
            setOpenBanner("ERROR");
        }
    };

    const handleDeleteQuest = (questId, questName) => {
        console.log(`[QuestsDashboard] Opening delete modal for quest ${questId}`);
        setPendingDeleteQuestId(questId);
        setPendingDeleteQuestName(questName || "Untitled Quest");
        setShowDeleteModal(true);
    };

    const confirmDeleteQuest = async () => {
        if (deleteText.toLowerCase() !== "delete") {
            setDeleteError("Type DELETE to proceed.");
            return;
        }

        try {
            console.log(`[QuestsDashboard] Attempting to delete quest ${pendingDeleteQuestId}`);
            const response = await deleteQuestAdmin(token, pendingDeleteQuestId);

            if (response && (response.status === 204 || response.status === 200)) {
                closeDeleteModal();
                setBannerMessage("Quest deleted successfully");
                setOpenBanner("MESSAGE");
                refetch();
                return;
            }

            setDeleteError("Error deleting quest. Please try again.");
        } catch (e) {
            console.error(e);
            setDeleteError("Error deleting quest. Please try again.");
        }
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeleteText("");
        setDeleteError("");
        setPendingDeleteQuestId(null);
        setPendingDeleteQuestName("");
    };

    return (
        <>
            <CardTable>
                <TableRow>
                    <Title title={"QUESTS OVERVIEW"} description={"Create, search and manage quests + steps."} />
                    <TableColumn>
                        <Button
                            variant={"OutlinedButton"}
                            label={"Create Quest"}
                            margin={"2rem 0 0 0"}
                            onClick={handleCreateNewQuest}
                        />
                    </TableColumn>
                </TableRow>
            </CardTable>

            <FilterContainer>
                <SearchBar
                    width="400px"
                    placeholder="Search quests by ID, name, description, starting room, active..."
                    onChange={handleSearchChange}
                />
            </FilterContainer>
            <FilterContainer>
                {searchInput.length !== 0 && filteredData.length === 0 && (
                    <MessageCenter signal>
                        <Typography variant="ConsoleDescription">Search Results:</Typography>
                        No quests found
                    </MessageCenter>
                )}
                {searchInput.length !== 0 && filteredData.length !== 0 && (
                    <MessageCenter>
                        <Typography variant="ConsoleDescription">Search Results:</Typography>
                        {filteredData.length === 1
                            ? ` ${filteredData.length} quest found`
                            : `${filteredData.length} quests found`}
                    </MessageCenter>
                )}
                {searchInput.length === 0 && (
                    <MessageCenter>
                        <Typography variant="ConsoleDescription">Total:</Typography>
                        {totalNumber} quests
                    </MessageCenter>
                )}
            </FilterContainer>

            <GridContainer>
                {filteredData.map((q) => {
                    const questId = q.questId || q.id;
                    const isActive = q.isActive ?? q.is_active ?? false;
                    const startingRoomId = startingRooms[q.questId] || q.startingRoomId || q.starting_room_id || "";
                    const createdAt = q.createdAt || "";
                    const updatedAt = q.updatedAt || "";

                    return (
                        <Card key={questId}>
                            <HeaderRow>
                                <Badge active={!!isActive}>
                                    {isActive ? (
                                        <>
                                            <Dot active /> Active
                                        </>
                                    ) : (
                                        <>
                                            <Dot /> Inactive
                                        </>
                                    )}
                                </Badge>
                                <TitleEditWrap>
                                    <TitleCard title={q.name}>{q.name || "Untitled Quest"}</TitleCard>
                                    <IconsContainer>
                                        {/* <IconButton onClick={() => handleEditQuest(questId)} title="Edit quest">
                                            <StyledEditIcon />
                                        </IconButton> */}
                                        <IconButton
                                            onClick={() => handleDeleteQuest(questId, q.name)}
                                            title="Delete quest"
                                        >
                                            <StyledTrashIcon />
                                        </IconButton>
                                    </IconsContainer>
                                </TitleEditWrap>
                            </HeaderRow>
                            <InfoRow>
                                <b>Quest ID:</b>
                                {questId}
                            </InfoRow>
                            <InfoRow>
                                <b>Starting Room:</b>
                                {startingRoomId || "-"}
                            </InfoRow>
                            <InfoRow>
                                <b>Description:</b>
                                {q.description || "-"}
                            </InfoRow>
                            <InfoRow>
                                <b>Created:</b>
                                {createdAt || "-"}
                            </InfoRow>
                            <InfoRow>
                                <b>Updated:</b>
                                {updatedAt || "-"}
                            </InfoRow>
                            <CardFooter style={{ justifyContent: "space-evenly" }}>
                                <ActionPill
                                    active={!!isActive}
                                    onClick={() => handleToggleQuestActive(questId, !!isActive)}
                                    title={isActive ? "Deactivate quest" : "Activate quest"}
                                >
                                    {isActive ? "Deactivate" : "Activate"}
                                </ActionPill>
                                <PrimaryAction onClick={() => handleEditQuest(questId)}>
                                    <span>Enter Quest builder</span>
                                    <Arrow>→</Arrow>
                                </PrimaryAction>
                            </CardFooter>
                        </Card>
                    );
                })}
            </GridContainer>

            <Banner
                message={bannerMessage}
                type={openBanner === "ERROR" ? "ERROR" : "MESSAGE"}
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
                topDisplay
                showCloseButton={false}
                timer={8000}
            />

            {/* Room Conflict Modal */}
            {showConflictModal && roomConflict && (
                <ModalOverlay onClick={() => setShowConflictModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>Room already has an active quest</ModalTitle>
                        <ModalBody>
                            This room already has an active quest: <strong>{roomConflict.name}</strong>. Only one active
                            quest is allowed per room.
                        </ModalBody>
                        <ModalButtons>
                            <Button
                                margin={"0"}
                                buttonWidth={"100%"}
                                variant="OutlinedButton"
                                label={`Deactivate "${roomConflict.name}"`}
                                onClick={handleDeactivateConflict}
                            />
                            <Button
                                margin={"0"}
                                variant="PinkButton"
                                label="Go to existing quest"
                                onClick={() => {
                                    setShowConflictModal(false);
                                    navigate(`/console/quest-builder/${roomConflict.questId}`);
                                }}
                            />
                            <Button
                                margin={"0"}
                                variant="DefaultButton"
                                label="Cancel"
                                onClick={() => {
                                    setShowConflictModal(false);
                                    setPendingActivateQuestId(null);
                                }}
                            />
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Delete Quest Confirmation Modal */}
            {showDeleteModal && (
                <ModalOverlay onClick={closeDeleteModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>Are you sure you want to delete quest "{pendingDeleteQuestName}"?</ModalTitle>
                        <ModalBody>
                            <WarningText>
                                This action is <YellowText>permanent</YellowText> and{" "}
                                <YellowText>irreversible.</YellowText>
                            </WarningText>
                            <WarningText style={{ marginTop: "1rem" }}>
                                Please type <strong>DELETE</strong> below to proceed.
                            </WarningText>
                            {deleteError && <DeleteErrorText>{deleteError}</DeleteErrorText>}
                            <DeleteInput
                                type="text"
                                value={deleteText}
                                onChange={(e) => setDeleteText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                            />
                        </ModalBody>
                        <ModalButtons>
                            <Button
                                margin={"0"}
                                variant="DefaultButton"
                                buttonWidth={"100%"}
                                label="GO BACK"
                                onClick={closeDeleteModal}
                            />
                            <Button
                                margin={"0"}
                                variant="PinkButton"
                                buttonWidth={"100%"}
                                disabled={deleteText.toLowerCase() !== "delete"}
                                label="PERMANENTLY DELETE"
                                onClick={confirmDeleteQuest}
                            />
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}
        </>
    );
};

export default QuestsDashboard;

// -------------------- styles (matching VanityLinks.js) --------------------

const CardTable = styled.div`
    display: flex;
    flex-direction: column;
`;

const TableRow = styled.div`
    display: flex;
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TableColumn = styled.div`
    flex: 1;
    @media (max-width: 768px) {
        font-size: 14px;
    }
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: flex-start;
    width: 100%;
`;

const MessageCenter = styled.div`
    color: ${(props) => (props.signal ? setColor.errorColor : setColor.successColor)};
    margin: 1rem 0rem;
    display: flex;
    justify-content: flex-end;
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 25px;
    margin: 20px 0;
    min-height: 100vh;
`;

const Card = styled.div`
    position: relative;
    background-color: rgb(23, 22, 34);
    color: #fff;
    border-radius: 12px;
    border: 1px solid ${setColor.modalOutline};
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;
    min-height: 340px;
    max-height: 340px;
    height: 340px;
    overflow: hidden;
    justify-content: flex-start;
    @media (max-width: 520px) {
        min-height: 320px;
        max-height: 320px;
        height: 320px;
    }
`;

const EditIconContainer = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    cursor: pointer;
    border-radius: 50%;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const StyledEditIcon = styled(EditIcon)`
    width: 25px;
    height: 25px;
    color: #aaa;
    cursor: pointer;
`;

const StyledTrashIcon = styled(TrashIcon)`
    width: 20px;
    height: 20px;
    cursor: pointer;
    path {
        fill: #fff;
    }
`;

const IconsContainer = styled.div`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const IconButton = styled.div`
    cursor: pointer;
    border-radius: 50%;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const CardFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: auto;
    gap: 10px;
`;

const ActionPill = styled.button`
    border: none;
    outline: none;
    border-radius: 8px;
    border: 1px solid ${(p) => (p.active ? setColor.errorColor : setColor.successColor)};
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 500;
    color: ${(p) => (p.active ? setColor.errorColor : setColor.successColor)};
    background: ${(p) => (p.active ? "rgba(255, 72, 66, 0.10)" : "rgba(147, 235, 199, 0.15)")};
    cursor: pointer;
    transition: background 0.2s;
    &:hover {
        background: ${(p) => (p.active ? "rgba(255, 72, 66, 0.25)" : "rgba(147, 235, 199, 0.25)")};
    }
`;

const DeleteIconButton = styled.button`
    border: none;
    background: transparent;
    color: ${setColor.errorColor};
    border-radius: 50%;
    height: 32px;
    width: 32px;
    cursor: pointer;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
`;

const Badge = styled.div`
    width: fit-content;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) => (p.active ? "rgba(147, 235, 199, 0.15)" : "rgba(255, 72, 66, 0.15)")};
    color: ${(p) => (p.active ? setColor.successColor : setColor.errorColor)};
`;

const Dot = styled.div`
    height: 10px;
    width: 10px;
    border-radius: 999px;
    background: ${(p) => (p.active ? setColor.successColor : setColor.errorColor)};
`;

const TitleEditWrap = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;
    max-width: 80%;
`;

const TitleCard = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    overflow-wrap: anywhere;
    word-break: break-word;
    margin: 0;
    flex: 1 1 0%;
    min-width: 0;
    line-height: 1.2;
    max-height: 2.6em;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    padding-right: 0.5em;
`;

const PrimaryAction = styled.button`
    display: inline-flex;
    gap: 10px;
    align-items: center;
    border-radius: 8px;
    border: 1px solid ${setColor.modalOutline};
    background: ${setColor.brightViolet100};
    color: #fff;
    cursor: pointer;
    padding: 8px 14px;
    font-size: 14px;
    font-weight: 600;
    transition: background 0.2s ease;
    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const Arrow = styled.span`
    font-weight: 1000;
`;

const InfoRow = styled.div`
    display: flex;
    gap: 8px;
    font-size: 14px;
    color: #aaa;
`;

// Room conflict modal styles
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
`;

const ModalContent = styled.div`
    background: rgb(23, 22, 34);
    border: 1px solid ${setColor.modalOutline};
    border-radius: 10px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    font-family: "Barlow-Regular", Helvetica;
`;

const ModalTitle = styled.h3`
    font-family: ${setFont.barlowCondensed};
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 1.3px;
    color: ${setColor.lightPink};
    margin: 0 0 16px 0;
`;

const ModalBody = styled.p`
    font-size: 14px;
    color: #fff;
    margin: 0 0 24px 0;
    line-height: 1.5;

    strong {
        color: ${setColor.successColor};
    }
`;

const ModalButtons = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    flex-wrap: wrap;

    @media (max-width: 500px) {
        flex-direction: column;
    }
`;

// Delete modal styles
const WarningText = styled.p`
    font-size: 14px;
    color: ${setColor.inputText};
    margin: 0;
`;

const YellowText = styled.span`
    color: ${setColor.yellowText || "#f5a623"};
    font-weight: 700;
`;

const DeleteInput = styled.input`
    padding: 10px;
    border: 1px solid ${setColor.grey1};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 1.5rem 0 0 0;
    width: 100%;
    background-color: transparent;
    font-family: "Barlow-Regular", Helvetica;

    &:focus {
        border: 1px solid ${setColor.accent4};
        outline: none;
    }
`;

const DeleteErrorText = styled.div`
    font-size: 14px;
    color: ${setColor.errorColor};
    margin-top: 1rem;
`;
